/**
 * Read-only member summary for the org Overview: who's in the org, with what
 * roles, at a glance. All management (add/remove, role edits, invitations,
 * reactivation) lives in the Members section — this list only links there
 * and to individual providers.
 */

import { Person } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';

import { IdChip } from './org-page-redesign/primitives';
import { RoleChips } from './role-chips';

const PREVIEW_LIMIT = 10;

interface MemberSummary {
  provider_id: string;
  name: string;
  email: string;
  roles: string[];
}

interface OrganizationMembersListProps {
  members: MemberSummary[];
  inactiveCount: number;
  onOpenMember: (providerId: string) => void;
  onManage: () => void;
}

export function OrganizationMembersList({
  members,
  inactiveCount,
  onOpenMember,
  onManage,
}: OrganizationMembersListProps) {
  const shown = members.slice(0, PREVIEW_LIMIT);
  const overflow = members.length - shown.length;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">
          Members
          <span className="ml-1.5 font-normal text-gray-400">{members.length}</span>
        </h3>
        <Button size="small" onClick={onManage}>
          Manage
        </Button>
      </div>

      {members.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No active members.
        </Typography>
      ) : (
        <div className="divide-y divide-gray-100">
          {/* Rows are divs, not buttons — the compact ID chip inside is
              itself a button (copies on click, full ID in its tooltip). */}
          {shown.map(member => (
            <div
              key={member.provider_id}
              onClick={() => onOpenMember(member.provider_id)}
              className="flex w-full cursor-pointer items-center gap-3 py-1.5 text-left text-sm hover:bg-gray-50"
            >
              <Person className="shrink-0 text-gray-300" fontSize="small" />
              <span className="w-48 shrink-0 truncate font-medium text-gray-900">
                {member.name || 'Unknown'}
              </span>
              <IdChip label="id" value={member.provider_id} compact />
              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                <RoleChips roles={member.roles} />
              </span>
              <span className="hidden max-w-xs truncate text-gray-500 sm:block">
                {member.email}
              </span>
            </div>
          ))}
        </div>
      )}

      {(overflow > 0 || inactiveCount > 0) && (
        <div className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-500">
          {overflow > 0 && `${overflow} more active`}
          {overflow > 0 && inactiveCount > 0 && ' · '}
          {inactiveCount > 0 && `${inactiveCount} inactive`}
          {' — '}
          <button type="button" onClick={onManage} className="text-indigo-600 hover:underline">
            see all in Members
          </button>
        </div>
      )}
    </section>
  );
}
